import { useState } from "react";
import { Delete, PhoneForwarded, UserPlus, UserX } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { formatPhone, normalizeE164 } from "../../lib/phone";
import { IconBtn } from "../ui";
import type { CallState } from "./CallProvider";

/**
 * Bringing somebody else onto the call.
 *
 * Takes the same slot as the keypad, so the window keeps one shape instead of
 * growing a block - and so the two cannot both be open, which matters: they are
 * both number entry, and two fields taking digits at once is a field the
 * operator has to look at to find out which.
 *
 * A typed field rather than the dial pad's twelve-key grid. This one is reached
 * mid-conversation from a keyboard the operator's hands are already on, usually
 * to enter a number somebody has just read out - and a second copy of the grid
 * would be twelve more buttons whose only job is to duplicate a keystroke.
 *
 * Nothing here decides anything. The two verbs are the server's (`lib/transfer.ts`
 * on the API side) and the difference between them is not cosmetic: "add" leaves
 * the operator on the call, "hand over" is the operator on their way out and
 * changes what the red button below does.
 */
export function TransferPad({
  state,
  onTransfer,
  onCancel,
}: {
  state: CallState;
  onTransfer: (to: string, mode: "add" | "handover") => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [raw, setRaw] = useState("");

  const live = state.transfer;
  const dialable = normalizeE164(raw) !== null;
  const busy = state.transferPending;

  // Somebody is already on their way, or here. The field goes away entirely
  // rather than being disabled: with one extension at a time there is nothing a
  // second number could be for, and an empty box under a live transfer reads as
  // an invitation to type into it.
  if (live) {
    const status = live.joined
      ? live.mode === "handover"
        ? t("calls.transfer.handedOver")
        : t("calls.transfer.inCall")
      : t("calls.transfer.ringing");
    return (
      <div className="mt-[22px] w-full">
        <div className="rounded-2xl bg-[var(--crm-sunk)] px-4 py-3.5 text-center">
          <b className="block text-[15px] font-bold tabular-nums tracking-[-0.02em]">
            {formatPhone(live.number)}
          </b>
          <span
            className={cn(
              "crm-live-st mt-2",
              // The same idle grey the connecting states use: a third party
              // whose phone is still ringing is not yet part of the call.
              !live.joined && "is-idle",
            )}
          >
            <i />
            {status}
          </span>
        </div>
        <button
          type="button"
          className="crm-btn mt-2.5 w-full justify-center"
          onClick={onCancel}
          disabled={busy}
        >
          <UserX className="size-4" />
          {t(live.joined ? "calls.transfer.drop" : "calls.transfer.cancel")}
        </button>
        <TransferError code={state.transferError} />
      </div>
    );
  }

  return (
    <div className="mt-[22px] w-full">
      <div className="mb-3 flex h-[46px] items-center gap-1.5 rounded-full bg-[var(--crm-sunk)] pl-5 pr-2">
        <input
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          spellCheck={false}
          placeholder={t("calls.transfer.placeholder")}
          aria-label={t("calls.transfer.placeholder")}
          className="min-w-0 flex-1 border-0 bg-transparent text-[16px] font-bold tabular-nums tracking-[-0.02em] outline-none"
          // Enter is the SAFE verb. The other one takes the operator off the
          // call, and a reflex keystroke is not consent to walk away from a
          // conversation.
          onKeyDown={(e) => {
            if (e.key === "Enter" && dialable && !busy) onTransfer(raw, "add");
          }}
        />
        <IconBtn
          className="border-0"
          onClick={() => setRaw("")}
          title={t("calls.dial.backspace")}
          aria-label={t("calls.dial.backspace")}
        >
          <Delete className="size-4" />
        </IconBtn>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className="crm-btn justify-center"
          onClick={() => onTransfer(raw, "add")}
          disabled={!dialable || busy}
          title={t("calls.transfer.addHint")}
        >
          <UserPlus className="size-4" />
          {t("calls.transfer.add")}
        </button>
        {/* Second, and deliberately not the default. It is the one that takes
            the operator off the call, and a mis-click on it cannot be undone by
            pressing the other one. */}
        <button
          type="button"
          className="crm-btn justify-center"
          onClick={() => onTransfer(raw, "handover")}
          disabled={!dialable || busy}
          title={t("calls.transfer.handoverHint")}
        >
          <PhoneForwarded className="size-4" />
          {t("calls.transfer.handover")}
        </button>
      </div>
      <TransferError code={state.transferError} />
    </div>
  );
}

/**
 * Why the last attempt was refused.
 *
 * Every code gets its own sentence, because they send the operator to different
 * places: a number on the do-not-call list is a decision somebody made, a
 * number Twilio says does not exist is a company to check, and the line they are
 * already talking on is a typo. A single "transfer failed" would send all three
 * of them to check their typing.
 *
 * ⚠ A code added on the server and not here silently becomes the generic
 * sentence, exactly as `callError.ts` warns for the dial route.
 */
const MESSAGES: Record<string, string> = {
  invalid_number: "calls.transfer.errInvalid",
  own_line: "calls.transfer.errOwnLine",
  already_on_call: "calls.transfer.errAlreadyOnCall",
  suppressed: "calls.transfer.errSuppressed",
  unreachable_number: "calls.transfer.errUnreachable",
  in_progress: "calls.transfer.errInProgress",
  not_bridged: "calls.transfer.errNotBridged",
  not_live: "calls.transfer.errNotLive",
  not_conference: "calls.transfer.errNotConference",
  handover_failed: "calls.transfer.errHandover",
  no_transfer: "calls.transfer.errNoTransfer",
};

function TransferError({ code }: { code: string | null }) {
  const t = useT();
  if (!code) return null;
  return (
    <p
      className="mt-2.5 text-center text-xs font-[600] text-[var(--color-warning,var(--color-destructive))]"
      role="alert"
    >
      {t(MESSAGES[code] ?? "calls.transfer.errGeneric")}
    </p>
  );
}
